import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Users, Store, DollarSign, Shield, Zap, BarChart3, Target, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Investors = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <img src="/nightbuzz-logo.png" alt="NightBuZz" className="h-10" />
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-16" data-testid="investor-hero">
          <p className="text-amber-400 font-semibold text-sm tracking-widest uppercase mb-3">Investor Summary</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            <span className="text-white">NIGHT</span>{' '}
            <span className="text-amber-400">BUZZ</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-2xl mx-auto">
            Where Nightlife Happens in Real Time
          </p>
        </div>

        {/* The Problem */}
        <section className="mb-16" data-testid="section-problem">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Target className="w-6 h-6 text-red-400" />
            The Problem
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="glass p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-purple-400 mb-2">For Patrons</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                "Where should we go tonight?" is a nightly question with no real-time answer. 
                Yelp shows ratings from last month, not who's there right now.
              </p>
            </Card>
            <Card className="glass p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-amber-400 mb-2">For Bar Owners</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                $1 billion+ is spent annually on nightlife marketing, yet most bars have zero 
                real-time visibility into who's walking through their doors or what's driving traffic.
              </p>
            </Card>
          </div>
        </section>

        {/* The Solution */}
        <section className="mb-16" data-testid="section-solution">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-400" />
            The Solution
          </h2>
          <p className="text-slate-300 mb-8 text-base">
            NightBuZz delivers live crowd data, social discovery, and direct monetization tools in one mobile-first platform.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="glass p-6 border border-purple-500/30">
              <h3 className="text-lg font-semibold text-purple-400 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                For Patrons (BuZZers)
              </h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><span className="text-purple-400 mt-0.5">-</span> Real-time Buzz Scores showing which bars are popping</li>
                <li className="flex items-start gap-2"><span className="text-purple-400 mt-0.5">-</span> Instagram-style Stories and social feed</li>
                <li className="flex items-start gap-2"><span className="text-purple-400 mt-0.5">-</span> Matchmaking with built-in safety notifications</li>
                <li className="flex items-start gap-2"><span className="text-purple-400 mt-0.5">-</span> Bar Crawl planning, Friends system, QR discounts</li>
                <li className="flex items-start gap-2"><span className="text-purple-400 mt-0.5">-</span> Premium perks and exclusive access</li>
              </ul>
            </Card>
            <Card className="glass p-6 border border-amber-500/30">
              <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center gap-2">
                <Store className="w-5 h-5" />
                For Bar Owners
              </h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> Live dashboard: who's checked in, demographics, headcount</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> Promoted Posts to boost visibility during slow nights</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> Singles Night bidding to drive guaranteed traffic</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> Specials management and photo uploads</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">-</span> Real-time analytics and patron insights</li>
              </ul>
            </Card>
          </div>
        </section>

        {/* Revenue Model */}
        <section className="mb-16" data-testid="section-revenue">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-emerald-400" />
            Revenue Model (4 Streams)
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: 'Bar Owner Subs', price: '$75-$150/mo', desc: '3 tiers, 60K+ US bars', color: 'amber' },
              { title: 'Patron Premium', price: '$2.99/mo', desc: 'Proven social model', color: 'purple' },
              { title: 'Promoted Posts', price: '$10/post', desc: 'Self-serve, high margin', color: 'emerald' },
              { title: 'Singles Bidding', price: 'Market-driven', desc: 'Event-based, recurring', color: 'pink' },
            ].map((item, i) => (
              <Card key={i} className={`glass p-5 border border-${item.color}-500/30 text-center`}>
                <p className={`text-${item.color}-400 font-bold text-2xl mb-1`}>{item.price}</p>
                <p className="text-white font-semibold text-sm">{item.title}</p>
                <p className="text-slate-400 text-xs mt-1">{item.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* The Ecosystem */}
        <section className="mb-16" data-testid="section-ecosystem">
          <h2 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-400" />
            The Ecosystem: How Everyone Wins
          </h2>
          <p className="text-slate-400 mb-8 text-sm">NightBuZz creates a three-way value loop where patrons, bar owners, and the platform all fuel each other's success.</p>

          {/* Paid Live Updates */}
          <Card className="glass p-6 border border-amber-500/30 mb-8">
            <h3 className="text-lg font-bold text-amber-400 mb-4">Paid Live Updates: Turning Dead Nights Into Packed Houses</h3>
            <p className="text-slate-300 text-sm mb-4">
              It's Tuesday at 7 PM. The bar is empty. The owner opens NightBuZz and creates a Promoted Post for $10:
            </p>
            <div className="bg-slate-800/60 rounded-lg p-4 border-l-4 border-amber-400 mb-4">
              <p className="text-white italic text-sm">"$3 margaritas until 9 PM — tonight only at The Blue Lounge!"</p>
            </div>
            <div className="space-y-3 text-sm text-slate-300">
              <div className="flex items-start gap-3">
                <span className="text-amber-400 font-bold flex-shrink-0">1.</span>
                <p>The post goes to the <strong className="text-white">top of every BuZZer's feed</strong> within a 12-mile radius — boosted and highlighted</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-amber-400 font-bold flex-shrink-0">2.</span>
                <p>Patrons see the deal, check the bar's <strong className="text-white">live Buzz Score</strong>, and decide to go</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-amber-400 font-bold flex-shrink-0">3.</span>
                <p>The first few patrons check in. The <strong className="text-white">Buzz Score rises</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-amber-400 font-bold flex-shrink-0">4.</span>
                <p>Other patrons see the score climbing — <strong className="text-white">they show up too</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-amber-400 font-bold flex-shrink-0">5.</span>
                <p>Stories and posts flow. <strong className="text-white">The $10 post turned a dead Tuesday into a profitable night</strong></p>
              </div>
            </div>
          </Card>

          {/* Three-Way Value */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="glass p-5 border border-purple-500/30">
              <h4 className="text-purple-400 font-semibold text-sm mb-3">Patrons Help Bar Owners</h4>
              <ul className="space-y-2 text-xs text-slate-300">
                <li>- Check-ins raise Buzz Score visibility</li>
                <li>- Stories = free authentic marketing</li>
                <li>- Sharing to X extends reach</li>
                <li>- RSVPs guarantee headcount</li>
                <li>- Ratings build reputation</li>
              </ul>
            </Card>
            <Card className="glass p-5 border border-amber-500/30">
              <h4 className="text-amber-400 font-semibold text-sm mb-3">Bar Owners Help Patrons</h4>
              <ul className="space-y-2 text-xs text-slate-300">
                <li>- Real-time specials save money</li>
                <li>- Photos show vibe before going</li>
                <li>- Singles Night creates social events</li>
                <li>- QR discounts reward loyalty</li>
                <li>- Updated info = no surprises</li>
              </ul>
            </Card>
            <Card className="glass p-5 border border-emerald-500/30">
              <h4 className="text-emerald-400 font-semibold text-sm mb-3">The App Powers Both</h4>
              <ul className="space-y-2 text-xs text-slate-300">
                <li>- Buzz Scores drive real-time discovery</li>
                <li>- Safety notifications build trust</li>
                <li>- Analytics enable smarter decisions</li>
                <li>- Promoted Posts = traffic on demand</li>
                <li>- Matchmaking = longer stays, higher tabs</li>
              </ul>
            </Card>
          </div>

          {/* Economics Table */}
          <Card className="glass p-6 border border-slate-700/50 mb-8">
            <h4 className="text-white font-semibold mb-4 text-sm">The Economics: A Real Example</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 text-slate-400 font-medium"></th>
                    <th className="text-center py-2 text-red-400 font-medium">Without NightBuZz</th>
                    <th className="text-center py-2 text-emerald-400 font-medium">With NightBuZz</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  <tr className="border-b border-slate-800"><td className="py-2">Tuesday night revenue</td><td className="text-center text-red-300">$400</td><td className="text-center text-emerald-300 font-semibold">$1,200</td></tr>
                  <tr className="border-b border-slate-800"><td className="py-2">Cost to promote</td><td className="text-center">$0 (no tools)</td><td className="text-center">$10</td></tr>
                  <tr className="border-b border-slate-800"><td className="py-2">Patrons through door</td><td className="text-center">15</td><td className="text-center text-emerald-300 font-semibold">50+</td></tr>
                  <tr><td className="py-2">Return on $10 spend</td><td className="text-center">—</td><td className="text-center text-emerald-300 font-bold">$800 incremental</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              A bar spending $150/mo on subscription + $40/mo on promoted posts could see $3,000–$8,000 in incremental monthly revenue.
            </p>
          </Card>

          {/* Flywheel */}
          <Card className="glass p-6 border border-purple-500/30">
            <h4 className="text-purple-400 font-semibold mb-4 text-sm">The Virtuous Cycle</h4>
            <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
              {['Bar posts a deal', 'Patrons show up', 'Buzz Score rises', 'More patrons discover it', 'Check-ins & stories flow', 'Free organic promotion', 'More revenue', 'Bar invests more'].map((step, i) => (
                <React.Fragment key={i}>
                  <span className="bg-slate-800 px-3 py-1.5 rounded-full text-slate-200 text-xs">{step}</span>
                  {i < 7 && <ArrowRight className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">
              Every participant makes the platform more valuable for everyone else. That's a marketplace with network effects.
            </p>
          </Card>
        </section>

        {/* Market Opportunity */}
        <section className="mb-16" data-testid="section-market">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-emerald-400" />
            Market Opportunity
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { stat: '$26.9B', label: 'US nightlife industry', src: 'IBIS World' },
              { stat: '65,000+', label: 'Bars & nightclubs in the US', src: '' },
              { stat: '72%', label: 'of Gen-Z discover nightlife via social', src: 'Eventbrite' },
              { stat: '0', label: 'Dominant real-time nightlife players', src: '' },
            ].map((item, i) => (
              <Card key={i} className="glass p-5 border border-slate-700/50 text-center">
                <p className="text-3xl font-bold text-emerald-400">{item.stat}</p>
                <p className="text-sm text-slate-300 mt-1">{item.label}</p>
                {item.src && <p className="text-xs text-slate-500 mt-1">{item.src}</p>}
              </Card>
            ))}
          </div>
        </section>

        {/* Competitive Advantage */}
        <section className="mb-16" data-testid="section-advantage">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-400" />
            Competitive Advantage
          </h2>
          <div className="space-y-4">
            {[
              { title: 'Real-Time Data Moat', desc: 'Every check-in, story, and wave enriches the platform. More users = better Buzz Scores = more users.' },
              { title: 'Dual-Sided Network', desc: 'Patrons get discovery; bar owners get intelligence. Both pay. Classic marketplace economics.' },
              { title: 'Safety as a Feature', desc: 'Emergency contact notifications create trust and reduce liability — a first in nightlife apps.' },
            ].map((item, i) => (
              <Card key={i} className="glass p-5 border border-slate-700/50">
                <h4 className="text-purple-400 font-semibold text-sm">{item.title}</h4>
                <p className="text-slate-300 text-sm mt-1">{item.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Traction */}
        <section className="mb-16" data-testid="section-traction">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-400" />
            Traction & Product Status
          </h2>
          <Card className="glass p-6 border border-emerald-500/30">
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-2"><span className="text-emerald-400">-</span> Fully built and deployed — not a prototype. Live with Stripe processing real transactions.</li>
              <li className="flex items-start gap-2"><span className="text-emerald-400">-</span> Integrated with Google Places, Yelp Fusion, Resend email, and cloud object storage.</li>
              <li className="flex items-start gap-2"><span className="text-emerald-400">-</span> Safety-first: mandatory emergency contacts, real-time email alerts on matches.</li>
              <li className="flex items-start gap-2"><span className="text-emerald-400">-</span> Google OAuth live, Apple Sign-In in development.</li>
              <li className="flex items-start gap-2"><span className="text-emerald-400">-</span> Mobile-first responsive design with real-time data infrastructure.</li>
            </ul>
          </Card>
        </section>

        {/* The Ask */}
        <section className="mb-16 text-center" data-testid="section-ask">
          <Card className="glass p-8 border border-amber-500/30 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-3">The Ask</h2>
            <p className="text-slate-300 mb-6">
              Seeking seed funding to scale user acquisition in 3 pilot cities, 
              hire a growth team, and build out the Singles Night bidding marketplace.
            </p>
            <p className="text-amber-400 font-bold text-lg" style={{ fontFamily: 'Space Grotesk' }}>
              NightBuZz isn't just an app — it's the operating system for nightlife.
            </p>
            <p className="text-slate-400 text-sm mt-2">Every bar. Every night. In real time.</p>
            <a
              href="/NightBuZz_Investor_Pitch_Deck.pptx"
              download
              className="inline-flex items-center gap-2 mt-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold px-8 py-3 rounded-full shadow-lg shadow-amber-500/25 transition-all hover:scale-105"
              data-testid="download-deck-btn"
            >
              Download Pitch Deck (PPTX)
            </a>
          </Card>
        </section>

        {/* Footer */}
        <div className="text-center text-slate-500 text-xs pb-8">
          <p>NightBuZz Confidential — For Investor Use Only</p>
        </div>
      </div>
    </div>
  );
};

export default Investors;
